import { inject } from '@angular/core';
import { Router, CanActivateFn, CanActivateChildFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRole } from '../models/user.model';

/**
 * Prefijos de ruta a los que el Contador SI tiene acceso dentro del panel Owner:
 * Finanzas, Caja (lectura) y Mi Suscripcion (para poder pagar el plan).
 * Todo lo demas (dashboard, productos, pedidos, reportes, settings, etc.) le
 * queda bloqueado. La autorizacion real es del backend; esto es defensa en
 * profundidad para que ni siquiera navegando por URL llegue al componente.
 */
const CONTADOR_ALLOWED_OWNER_PREFIXES: readonly string[] = [
  '/owner/finanzas',
  '/owner/cash-registers',
  '/owner/subscription',
];

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoggedIn()) {
    return true;
  }

  router.navigate(['/auth/login'], {
    queryParams: { returnUrl: state.url }
  });
  return false;
};

export const roleGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Soporta tanto un rol individual como un array de roles
  const roleData = route.data['role'];
  const rolesData = route.data['roles'];

  let allowedRoles: UserRole[] = [];

  if (roleData) {
    // Rol individual
    const requiredRole = UserRole[roleData as keyof typeof UserRole];
    allowedRoles = [requiredRole];
  } else if (rolesData && Array.isArray(rolesData)) {
    // Array de roles
    allowedRoles = rolesData.map(roleString =>
      UserRole[roleString as keyof typeof UserRole]
    );
  }

  if (authService.isLoggedIn() && allowedRoles.some(role => authService.hasRole(role))) {
    return true;
  }

  router.navigate(['/unauthorized']);
  return false;
};

/**
 * Guard de redireccion del home del panel Owner (`/owner`). El Contador no ve el
 * dashboard del Owner: su primera seccion permitida es Finanzas, asi que se le
 * redirige alli. El resto de roles (Owner/Manager) cae al dashboard por defecto.
 */
export const ownerHomeRedirectGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.hasRole(UserRole.Contador)) {
    router.navigate(['/owner/finanzas']);
    return false;
  }

  router.navigate(['/owner/dashboard']);
  return false;
};

/**
 * Restringe al Contador, dentro del shell del Owner, SOLO a Finanzas, Caja
 * (lectura) y Mi Suscripcion. Para cualquier otra ruta del panel lo manda a su home permitido
 * (Finanzas). Los demas roles (Owner/Manager) pasan sin cambios.
 * Se aplica como `canActivateChild` en la ruta del layout del Owner: corre en
 * cada navegacion interna, asi un Contador no alcanza dashboard/productos/
 * pedidos/reportes/settings ni por URL directa.
 */
export const contadorAccessGuard: CanActivateChildFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.hasRole(UserRole.Contador)) {
    return true;
  }

  const url = state.url;
  const allowed = CONTADOR_ALLOWED_OWNER_PREFIXES.some(
    prefix => url === prefix || url.startsWith(prefix + '/') || url.startsWith(prefix + '?')
  );
  if (allowed) {
    return true;
  }

  router.navigate(['/owner/finanzas']);
  return false;
};
